// External
import React, { Suspense, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import 'remixicon/fonts/remixicon.css';

import { BrowserRouter, Route, Routes } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
// import 'bootstrap/dist/css/bootstrap.min.css'; //test
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AppLayout from './Deepecho/AppLayout';

import GlobalContextProvider from './Deepecho/state/GlobalContextProvider';
import { ProtectedPages } from './Deepecho/Pages';
import publicPages from './Deepecho/PublicPages';
import DefaultLayout from './Deepecho/DefaultLayout';
import ProtectedRoutes from './Deepecho/ProtectedRoutes';
import TwoFactorSetupPage from './Deepecho/UI/Pages/TwoFactorSetup/TwoFactorSetup.page';
import PublicRoutes from './Deepecho/PublicRoutes';
import SharedReportPage from './Deepecho/UI/Pages/SharedReport/shared-report.page';

import { QueryClient, QueryClientProvider, keepPreviousData } from '@tanstack/react-query';
import NotFoundRedirect from './Deepecho/UI/ComponnentsUI/common/NotFound';
import { CategoriesProvider } from './Deepecho/contexts/CategoriesContext';
import { FilterOptionsProvider } from './Deepecho/contexts/FilterOptionsContext';




function App() {



  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 1,
            // Keep the old payload visible while a refetch runs (e.g. facility
            // switch). Pages that check `isFetching` can render a subtle overlay
            // instead of unmounting into a full-page loader.
            placeholderData: keepPreviousData,
          },
        },
      })
  );





  return (
    <div className="bg-card-color-200">
      <QueryClientProvider client={queryClient}>
        <CategoriesProvider>
          <FilterOptionsProvider>
          <>
            <BrowserRouter basename="/">
              <Suspense fallback={<div>Loading...</div>}>
                <ToastContainer />
                <Routes>

                  {/* Patient exam share. Deliberately bare: no PublicRoutes
                      (it redirects logged-in users to /home, which would stop a
                      clinician opening a link they just created) and no
                      DefaultLayout (the recipient is not a user of this app). */}
                  <Route
                    path="/shared/:token"
                    element={<SharedReportPage />}
                  />

                  {/* Any other shape of /shared/ URL is a mangled link. It has to
                      be caught here, because the "*" route below redirects to
                      /home, which is protected and bounces to /login: a patient
                      with no account would be asked to sign in. The page reads a
                      missing token as an unavailable link and says so. */}
                  <Route path="/shared/*" element={<SharedReportPage />} />

                  <Route
                    path="/2fa-setup"
                    element={
                      <PublicRoutes>
                        <TwoFactorSetupPage />
                      </PublicRoutes>
                    }
                  />

                  {ProtectedPages.filter(
                    (page) => page.path !== '/2fa-setup'
                  ).map((page, key) => (
                    <Route
                      key={key}
                      path={page.path}
                      element={
                        <ProtectedRoutes requiredPermissions={page.requiredPermissions}>
                          <GlobalContextProvider>
                            <AppLayout>{page.element}</AppLayout>
                          </GlobalContextProvider>
                        </ProtectedRoutes>
                      }
                    />
                  ))}

                  {publicPages.map((page, key) => (
                    <Route
                      key={key}
                      path={page.path}
                      element={
                        <PublicRoutes>
                          <DefaultLayout>{page.element}</DefaultLayout>
                        </PublicRoutes>
                      }
                    />
                  ))}
                  <Route path="*" element={<NotFoundRedirect />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </>
          </FilterOptionsProvider>
        </CategoriesProvider>
      </QueryClientProvider>
    </div>
  );
}

App.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string.isRequired,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
    }),
  ]).isRequired,
};

App.defaultProps = {
  config: {
    /**
     * Relative route from domain root that OHIF instance is installed at.
     * For example:
     *
     * Hosted at: https://ohif.org/where-i-host-the/viewer/
     * Value: `/where-i-host-the/viewer/`
     * */
    routerBaseName: '/',
    /**
     *
     */
    showLoadingIndicator: true,
    showStudyList: true,
    oidc: [],
    extensions: [],
  },
  defaultExtensions: [],
};

export default App;
